// web/src/components/ds/DsModal.tsx — DS.7 (sprint aos-ds-settings-modals,
// spec §2 D6b / D10 / D18 / D8b).
//
// THREE things live in this module, and they are deliberately in ONE file
// (`doc-format.md` §One truth) because the sibling wrappers must share them:
//
//   1. `layerStack` — the campaign's overlay stack. The vendored DS knows
//      NOTHING about which overlay is on top: 0 hits for
//      topmost/layerStack/DismissableLayer over the whole tree, and EIGHT
//      overlay-class pieces each subscribe their OWN `document` keydown
//      listener while open (the 7 under `overlays/` including `Menu`, plus
//      `feedback/Dialog`) `[medido at v0.2.0, 2026-09-14 — spec §0.17b
//      reproduces exactly]`. Radix shipped this guard for free
//      (`DismissableLayer`); the DS does not. Without it ONE Escape closes
//      BOTH halves of a live path this app already has:
//      `ManageStagesDialog` opens `ConfirmDialog` (spec §0.13d), and so
//      does `RunnersTable`.
//
//      D18 is a Level-4 fix (`universal-principles.md` §Error Response
//      Hierarchy — eliminate the cause), not a guard per call site: DS.7 is
//      only the FIRST consumer. DS.5's `DsMenu`, DS.6's composer overlays
//      and DS.8's wrappers register with THIS stack as they land (§6 row 11).
//
//      THE GUARD RUNS AT EVENT TIME, INSIDE THE CALLBACK — never as a
//      render-time `onClose={isTop ? onClose : noop}`. Why, measured: the
//      DS `Modal` copies `onClose` into a ref on every render
//      (Modal.tsx:64-65) and its keydown handler is a `useCallback(…, [])`
//      that reads `onCloseRef.current()` at EVENT time (:67-69, subscribed
//      at :101). A render-time `isTop` is computed BEFORE the inner
//      overlay's effect pushes its id, and nothing re-renders the outer
//      afterwards — so the outer would keep a live `onClose` and one Escape
//      would close both. Drill (a6) is that formulation's tripwire.
//
//   2. `useFocusTrap` — the trap `@ds/overlays/SideSheet` does not have.
//      PORTED, not invented: it re-expresses the DS `Modal`'s own inline
//      implementation (Modal.tsx:72-86) as a hook so the wrappers can reuse
//      it (`universal-principles.md` §Horizontal Reuse). `DsModal` does NOT
//      call it — the piece already traps, and a second trap on the same
//      subtree would fight the first (two `preventDefault` handlers on one
//      Tab). `DsSideSheet` does. `DsLightbox` is an ENUMERATED EXEMPTION
//      (D8b): two focusables, Escape and backdrop both covered.
//
//   3. `DsModal` — the product-block wrapper the six modal call sites use,
//      so the size map, the layer registration and the testid convention
//      are decided ONCE instead of six times (D6b's rejected alternative).
//
// ACCEPTED DELTAS vs Radix, recorded in the results doc, not engineered
// around: `{...props}` spreads onto the piece's OVERLAY div, not its content
// box (the piece exposes no `contentProps`), so `data-testid` lands on the
// overlay — every `getByTestId(...)`/`within(...)` query still resolves
// because the overlay contains the card, and the two Playwright clips become
// full-viewport (D10, D9's accepted diffs). The close button's
// `aria-label="Close modal"` is hardcoded with no prop (§0.18 class (ii)) —
// a11y-only, one upstream issue (§6 row 6), never a vendor patch (DS-C4).

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ComponentProps,
  type RefObject,
} from 'react';
import Modal from '@ds/overlays/Modal';

/**
 * The campaign's overlay stack — mounted here, consumed by every `ds/`
 * overlay wrapper in every wave. Exported so there is exactly ONE.
 */
export const layerStack: symbol[] = [];

/**
 * Register an overlay instance on `layerStack` while `active`, and hand back
 * a predicate that answers "am I the topmost layer?" AT CALL TIME.
 *
 * The entry is spliced in the effect CLEANUP, so an unmount cannot leak one:
 * a stale entry would keep the overlay beneath it non-topmost forever and
 * Escape could never close it (drill (a7)).
 */
export function useOverlayLayer(active: boolean): () => boolean {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('ds-overlay-layer');

  useEffect(() => {
    if (!active) return;
    const id = idRef.current!;
    layerStack.push(id);
    return () => {
      const at = layerStack.lastIndexOf(id);
      if (at !== -1) layerStack.splice(at, 1);
    };
  }, [active]);

  return useCallback(() => layerStack[layerStack.length - 1] === idRef.current, []);
}

/* ------------------------------------------------------------------ *
 * ARIA-HIDE OUTSIDE — the second thing Radix gave this app for free.
 * ------------------------------------------------------------------ */

// Radix's `Dialog` marks everything outside the open dialog `aria-hidden`
// while it is open. No DS overlay does `[medido at v0.2.0: grep -rn
// 'aria-hidden' over overlays/ -> only the decorative backdrop nodes]`, and
// the gap is not theoretical: `ManageStagesDialog` opens `ConfirmDialog` over
// a list whose rows each carry a delete button labelled "Eliminar", and the
// confirm's own button is labelled "Eliminar" too. With the page still
// exposed, `getByRole('button', { name: /eliminar/i })` matches three
// elements — which is a test failing loudly about the real defect behind it:
// a screen reader reads the page BEHIND an open modal, and the founder's
// confirm is one of several identically-named buttons.
//
// Ref-counted per element, so nested overlays (which hide each other's hosts)
// restore in any close order. Restores the exact previous value, so a node
// that was already `aria-hidden` for its own reasons stays that way.
const hiddenCounts = new WeakMap<Element, number>();
const previousAriaHidden = new WeakMap<Element, string | null>();

function hideOutside(node: Element): () => void {
  const touched: Element[] = [];
  let walker: Element | null = node;
  while (walker && walker.parentElement) {
    const parent: HTMLElement = walker.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (sibling === walker) continue;
      if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
      const count = hiddenCounts.get(sibling) ?? 0;
      if (count === 0) previousAriaHidden.set(sibling, sibling.getAttribute('aria-hidden'));
      hiddenCounts.set(sibling, count + 1);
      sibling.setAttribute('aria-hidden', 'true');
      touched.push(sibling);
    }
    walker = parent === document.body ? null : parent;
  }
  return () => {
    for (const el of touched) {
      const count = (hiddenCounts.get(el) ?? 1) - 1;
      hiddenCounts.set(el, count);
      if (count > 0) continue;
      const before = previousAriaHidden.get(el) ?? null;
      if (before === null) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', before);
      previousAriaHidden.delete(el);
    }
  };
}

/** Mark everything outside `ref`'s subtree `aria-hidden` while `active`. */
export function useAriaHideOutside(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    return hideOutside(node);
  }, [ref, active]);
}

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Trap Tab inside `ref`'s subtree while `active`, wrapping in both
 * directions, and return focus to whatever was focused on activation.
 * Same predicate as the DS `Modal`'s own trap (Modal.tsx:72-86).
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const restoreTo = document.activeElement as HTMLElement | null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = node.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;
      // literal index: the early return above guarantees a non-empty list.
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreTo?.focus?.();
    };
  }, [ref, active]);
}

export type DsModalProps = ComponentProps<typeof Modal>;

export const DsModal = forwardRef<HTMLDivElement, DsModalProps>(function DsModal(
  { open, onClose, initialFocusRef, ...rest },
  ref,
) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const isTopmost = useOverlayLayer(open);
  useAriaHideOutside(overlayRef, open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Stable by construction (empty deps, refs read at event time) — the piece
  // stores it in a ref anyway, and a render-time guard is the bug D18 kills.
  const handleClose = useCallback(() => {
    if (isTopmost()) onCloseRef.current();
  }, [isTopmost]);

  // THE LATE-FOCUS GUARD, and it is a behaviour fix, not a test workaround.
  // Declared ABOVE `focusTarget`: its getter closes over this ref, and a
  // future DS `Modal` that read `initialFocusRef.current` during render
  // instead of from its post-commit timer would hit the temporal dead zone
  // (reviewer carry-forward, 2026-09-14). Ordering costs nothing.
  const initialFocusRefLive = useRef(initialFocusRef);
  initialFocusRefLive.current = initialFocusRef;

  // The piece focuses on a `setTimeout(…, 50)` (Modal.tsx:102-110) — Radix
  // focused synchronously on open — and it reads `initialFocusRef.current`
  // INSIDE that callback. So anything the user does in the first 50 ms is
  // undone: `EditProjectModal.test.tsx` starts typing a repo URL into the
  // second field, the timer fires mid-word, focus jumps to the first field
  // and the rest of the URL lands in a 2-character initials box. Measured,
  // not theorised — that test reds without this guard, and the same class
  // costs a fast founder his first keystrokes.
  //
  // The fix is to make the ref ANSWER AT CALL TIME: if focus is already
  // inside THIS modal's own overlay, the requested element IS whatever has it,
  // so the piece's `requested.focus()` is a no-op; otherwise the host's
  // requested element (or null, which lets the piece fall through to its first
  // focusable). Upstream shape: the piece should skip the steal when focus is
  // already inside its own content — bundled into the single upstream issue.
  //
  // The containment test is `overlayRef.contains`, NOT `closest('[role=dialog]')`
  // (reviewer, 2026-09-14): the loose form matched ANY dialog, so on the live
  // nested path (`ManageStagesDialog` → `ConfirmDialog`, `RunnersTable` →
  // `ConfirmDialog`) the confirm opened while focus stayed on the OUTER
  // modal's trigger — which this branch's own `aria-hidden` had just hidden.
  // Measured: 200 ms after open, `activeElement` was still the «Eliminar»
  // button behind the confirm, so Enter re-fired it; Radix moved focus in on
  // open, so the loose form was a regression against the exact scenario the
  // layer stack exists for.
  const focusTarget = useRef<React.RefObject<HTMLElement | null>>({
    get current(): HTMLElement | null {
      const active = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
      if (active && overlayRef.current?.contains(active)) return active;
      return initialFocusRefLive.current?.current ?? null;
    },
  }).current;

  return (
    <Modal
      ref={(node: HTMLDivElement | null) => {
        overlayRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      open={open}
      onClose={handleClose}
      initialFocusRef={focusTarget}
      {...rest}
    />
  );
});

export default DsModal;
